import React from 'react'
import { Button, IconEcont, IconSpeedy, Price, Typography } from '../Common'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import {
  myOrdersApi,
  useOrder,
  usePrice,
  useOrderPrice,
  useOrderShippingPrice,
  useShippingAddress,
  useBillingAddress,
  useGlobalLabels,
} from '@sentecacommerce-theme/lib'
import { OrderProps } from './order.props'
import { AffiliateScripts, ProductPreview } from './Components'

const iconsMap: any = {
  Speedy: <IconSpeedy style={{ width: '6.8rem', height: '3.4rem' }} />,
  Econt: (
    <IconEcont
      style={{
        width: '8.8rem',
        height: '5rem',
        marginTop: '-1.6rem',
        marginBottom: '-2.2rem',
        marginLeft: '-1.8rem',
      }}
    />
  ),
  Evropat: (
    <img
      src="https://production.hippoland-contents.com/platform/media/evroput-logo-13845.jpg"
      alt="Европът"
      style={{ width: '6.8rem', height: 'auto' }}
    />
  ),
}

export const Order = ({ labels }: OrderProps) => {
  const router = useRouter()

  const globalLabels = useGlobalLabels()

  const orderId = router.query?.orderId as string

  const isSuccess = Boolean(router.query?.success)

  const { data } = myOrdersApi.useGetById(orderId)

  const { orderRefNumber, status, lineItems, discountCodes } = useOrder({
    data,
  })

  const price = useOrderPrice(data)
  const shippingPriceRaw = useOrderShippingPrice(data?.shippings?.[0])

  const {
    price: shippingPrice,
    discountPrice: shippingDiscountPrice,
  } = usePrice({
    price: shippingPriceRaw.originalPrice,
    discountedPrice: shippingPriceRaw.discountedPrice,
  })

  const hasShipping = !shippingDiscountPrice?.displayValue
  const { price: subtotal } = usePrice({
    price: price.subtotal,
    discountedPrice: price.discountedSubtotal,
  })

  const { price: totalWithShippingPrice } = usePrice({
    price: price.taxedPrice,
  })

  const {
    customer,
    isOffice,
    city,
    street,
    quarter,
    office,
    streetNumber,
    building,
    postalCode,
    courier,
    entrance,
    additionalAddressInfo,
    apartment,
    floor,
  } = useShippingAddress(data?.shippingAddress)

  const billingAddress = useBillingAddress(data?.billingAddress)

  const courierIcon = iconsMap[courier as any]

  const getAddress = () => {
    if (!street) return

    let result = `${street}`

    if (streetNumber) {
      result += `, ${globalLabels?.nr}${streetNumber}`
    }

    if (entrance) {
      result += `, ${globalLabels?.address?.vh} ${entrance}`
    }

    if (floor) {
      result += `, ${globalLabels?.address?.et} ${floor}`
    }

    if (apartment) {
      result += `, ${globalLabels?.address?.ap} ${apartment}`
    }

    return result
  }

  const hasAddress = Boolean(office ? office : getAddress())

  const getQuarterInfo = () => {
    if (hasAddress) return ''

    let result = ''

    if (streetNumber) {
      result += `, ${globalLabels?.nr}${streetNumber}`
    }

    if (entrance) {
      result += `, ${globalLabels?.address?.vh} ${entrance}`
    }

    if (floor) {
      result += `, ${globalLabels?.address?.et} ${floor}`
    }

    if (apartment) {
      result += `, ${globalLabels?.address?.ap} ${apartment}`
    }

    return result
  }

  const isStepTwoActive = status === 'Confirmed' || status === 'Complete'
  const isThreeTwoActive = status === 'Complete'

  return (
    <Container>
      {status !== 'Cancelled' && (
        <Header>
          <Step active label={labels?.orderStatus?.accepted} />
          <Line />
          <Step
            label={labels?.orderStatus?.processing}
            active={isStepTwoActive}
          />
          <Line />
          <Step
            label={labels?.orderStatus?.sending}
            active={isThreeTwoActive}
          />
        </Header>
      )}

      {status === 'Cancelled' && (
        <CanceledOrderText>{labels?.orderStatus?.cancelled}</CanceledOrderText>
      )}

      {isSuccess && (
        <>
          <Title>
            {`${labels?.thankYou} ${customer?.firstName} ${customer?.lastName}`}
          </Title>
          <Title className="my-4">
            {labels?.orderStatus?.orderSendSuccess}
          </Title>
          <AffiliateScripts data={data} />
        </>
      )}
      <OrderNumber>
        {`${labels?.orderNumber}:`} <strong>{orderRefNumber}</strong>
      </OrderNumber>
      <div className="row g-4 mt-5">
        <InfoBox>
          <InfoBoxContainer>
            <BoxTitle>{labels?.clientInfo}</BoxTitle>
            <BoxContent>
              <Row>
                <Label>{globalLabels?.personalInformation?.firstName}</Label>
                <Info>
                  {customer?.firstName} {customer?.lastName}
                </Info>
              </Row>
            </BoxContent>
            <BoxContent>
              <Row>
                <Label>{globalLabels?.personalInformation?.email}</Label>
                <Info>{customer?.email}</Info>
              </Row>
            </BoxContent>
            <BoxContent>
              <Row>
                <Label>{globalLabels?.personalInformation?.number}</Label>
                <Info>{customer?.phone}</Info>
              </Row>
            </BoxContent>
            <BoxContent>
              <Row>
                <Label>{labels.usedVouchers}</Label>
                <Info>
                  {discountCodes?.map((code, i: number) => {
                    return (
                      <span key={`Voucher__${i}`}>
                        {code}
                        {discountCodes?.length - 1 !== i && <>, &nbsp;</>}
                      </span>
                    )
                  })}
                </Info>
              </Row>
            </BoxContent>
          </InfoBoxContainer>
        </InfoBox>
        <InfoBox>
          <InfoBoxContainer>
            <BoxTitle>
              {office
                ? globalLabels?.address?.office
                : globalLabels?.address?.label}{' '}
              {labels?.forDelivery}
            </BoxTitle>
            {courierIcon}
            <BoxContent>
              {!isOffice && (
                <>
                  <Row>
                    <Info>
                      {city?.name} {postalCode}
                    </Info>
                  </Row>

                  {Boolean(quarter) && (
                    <Row>
                      <Info>
                        {Boolean((quarter as any)?.type) && (
                          <span>{(quarter as any)?.type}. </span>
                        )}
                        {quarter?.name} {getQuarterInfo()}
                      </Info>
                    </Row>
                  )}
                  {Boolean(street) && (
                    <Row>
                      <Info>
                        <span>{`${globalLabels?.address?.ul} `}</span>
                        {getAddress()}
                      </Info>
                    </Row>
                  )}
                  {Boolean(building) && (
                    <Row>
                      <Info>
                        <span>{`${globalLabels?.address?.building}: ${building}`}</span>
                      </Info>
                    </Row>
                  )}
                </>
              )}
              {isOffice && (
                <>
                  <Row>
                    <Info>{`${globalLabels?.address?.office}, ${office?.name}`}</Info>
                  </Row>
                  <Row>
                    <Info>{(office as any)?.address?.fullAddress}</Info>
                  </Row>
                </>
              )}
              {additionalAddressInfo && (
                <Row>
                  <Ellipsis>* {additionalAddressInfo}</Ellipsis>
                </Row>
              )}
            </BoxContent>
          </InfoBoxContainer>
        </InfoBox>
        {Boolean(billingAddress) && (
          <div className="col-12">
            <InfoBoxContainer>
              <BoxTitle className="pb-1">{labels?.invoiceData}</BoxTitle>
              <BoxContent>
                <Row>
                  <Info>
                    {billingAddress?.companyPerson}
                    {', '}
                    {billingAddress?.companyName}
                  </Info>
                </Row>
                <Row>
                  <Info>#{billingAddress?.vatId}</Info>
                </Row>
                <Row>
                  <Info>{billingAddress?.companyAddress}</Info>
                </Row>
              </BoxContent>
            </InfoBoxContainer>
          </div>
        )}
      </div>
      <ProductsList>
        {lineItems?.map((item: any) => (
          <ProductPreview item={item} key={`Product__${item.image}`} />
        ))}
      </ProductsList>
      <SummaryContainer>
        <SummaryBox isGreen>
          <div>
            <SummaryLabel>{`${globalLabels?.delivery}:`}</SummaryLabel>
          </div>
          <div>
            {Boolean(shippingPrice.value) && (
              <Price
                currency={shippingPrice.currency}
                size="small"
                price={
                  !hasShipping && shippingDiscountPrice
                    ? shippingDiscountPrice
                    : shippingPrice.value
                }
              />
            )}
          </div>
        </SummaryBox>
        <SummaryBox>
          <div>
            <SummaryLabel>{`${globalLabels?.total}:`}</SummaryLabel>
          </div>
          <div>
            {Boolean(subtotal?.value) && (
              <Price
                currency={subtotal.currency}
                size="small"
                price={totalWithShippingPrice.value}
              />
            )}
          </div>
        </SummaryBox>
      </SummaryContainer>
      <ButtonContainer>
        <Button link="/" fullWidth variant="green" borderRadius="small">
          {globalLabels?.continueWithShopping}
        </Button>
      </ButtonContainer>
    </Container>
  )
}

const Header = styled.div.attrs({
  className: 'd-flex justify-content-center align-items-center mx-auto',
})`
  margin-bottom: 6rem;
  max-width: 68rem;
`

const Container = styled(Typography).attrs({
  as: 'div',
  variant: 4,
  className: 'container mt-5 pt-5',
})`
  max-width: 90rem;
`

const CanceledOrderText = styled.p.attrs({
  className: 'mb-5',
})`
  color: ${({ theme }) => theme.palette.danger};
  text-align: center;
`

const Title = styled.div`
  text-align: center;
`

const ProductsList = styled.ul.attrs({
  className: 'row g-5 pt-5',
})`
  list-style: none;
`

const Product = styled.li.attrs({
  className: 'col-12',
})``

const Shadow = styled.div.attrs({
  className: 'p-4 d-flex',
})`
  border-bottom: 0.1rem solid ${({ theme }) => theme.palette.lightgrey};
`

const OrderNumber = styled(Typography).attrs({
  as: 'p',
  variant: 6,
})`
  text-align: center;
`

const Cap = styled.span`
  text-transform: capitalize;
`

const InfoBox = styled.div.attrs({
  className: 'col-12 col-lg-6',
})``

const InfoBoxContainer = styled.div.attrs({
  className: 'p-4',
})`
  height: 100%;
  border: 0.1rem solid ${({ theme }) => theme.palette.lightgrey};
  border-radius: 0.4rem;
`

const BoxTitle = styled.p`
  font-weight: bold;
`

const BoxContent = styled(Typography).attrs({
  as: 'div',
  variant: 6,
})``

const Label = styled.p``

const Info = styled.p``

const Row = styled.div.attrs({
  className: 'mt-3',
})``

const Step = styled(Typography).attrs({
  as: 'div',
  variant: 8,
})<{ label?: string; active?: boolean }>`
  width: 1.2rem;
  height: 1.2rem;
  border-radius: 50%;
  background-color: ${(props) =>
    props.active
      ? props.theme.palette.silverTree
      : props.theme.palette.secondary};
  position: relative;
  border: 0.2rem solid ${({ theme }) => theme.palette.silverTree};
  &:after {
    content: '${(props) => props.label}';
    opacity: ${(props) => (props.active ? 1 : 0.4)};
    position: absolute;
    width: fit-content;
    max-width: none;
    white-space: nowrap;
    left: 0;
    top: 2rem;
    transform: translateX(-50%);
  }
`

const Line = styled.div`
  width: calc(100% / 2 - 8rem);
  height: 0.3rem;
  background-color: ${({ theme }) => theme.palette.silverTree};
`

const ImageContainer = styled.div.attrs({
  className: 'mr-3',
})`
  width: 16rem;
`

const ContentContainer = styled.div`
  & > h5 {
    font-weight: normal;
  }
`

const PriceContainer = styled.div.attrs({
  className: 'd-flex ml-auto justify-content-end align-items-start',
})`
  min-width: 10rem;
`

const SKU = styled(Typography).attrs({
  as: 'span',
  variant: 7,
  className: 'd-block mt-1 mb-3',
})`
  color: ${({ theme }) => theme.palette.greyTitle};
`

const Quantity = styled(Typography).attrs({
  as: 'span',
  variant: 6,
  className: 'd-block',
})``

const ButtonContainer = styled.div.attrs({
  className: 'mx-auto mt-5 pt-4 pb-5',
})`
  max-width: 30rem;
`

const SummaryContainer = styled.div.attrs({
  className: 'mt-5',
})`
  display: table;
  margin-left: auto;
`

const SummaryBox = styled.div<{ isGreen?: boolean }>`
  display: table-row;

  & > div {
    display: table-cell;
    padding-top: 1rem;

    & > div {
      justify-content: flex-end;
    }
  }

  &:first-of-type {
    & > div {
      padding-top: 0;
    }
  }

  * {
    color: ${(props) =>
      props.isGreen ? props.theme.palette.silverTree : 'inherit'};
  }
`

const SummaryLabel = styled(Typography).attrs({
  as: 'p',
  variant: 5,
  className: 'd-block mr-3',
})`
  min-width: 9rem;
`
const Ellipsis = styled(Typography)`
  max-width: 37rem;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`
